import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { COMMENT_MAX_LENGTH, type CommentItem } from "@/entities/comment";
import { ApiError, detailString } from "@/shared/api";
import { Button, Sheet, StarRating, TextArea } from "@/shared/ui";

import { useCreateComment, useEditComment } from "../model/useWriteComment";

/**
 * Форма отзыва: публикация и правка (доработки §6.1).
 *
 * **Предупреждение об имени — обязательная часть формы, а не подсказка.**
 * Отзывы показывают настоящее имя и фотографию автора, в отличие от рейтинга и
 * подиума ТОП-3, где действует opt-in §5. Человек соглашается на это здесь и
 * сейчас, нажимая «Отправить», — и потому обязан прочитать, под чем
 * подписывается, ДО отправки, а не увидев своё фото на витрине робота. Убрать
 * эту строку значит превратить осознанное продуктовое решение в утечку.
 *
 * Ошибки бэкенда показываются у поля, а не тостом: причина отказа («слишком
 * коротко», «такой отзыв уже был», «время правки истекло») относится к тексту,
 * и искать её сверху экрана человеку незачем. Текст берётся из i18n по коду
 * `details.reason` — сообщение бэкенда служит запасным вариантом для случаев,
 * которых фронт ещё не знает.
 */
interface CommentComposerProps {
  robotId: number;
  /** Есть — правим существующий отзыв, нет — пишем новый. */
  comment?: CommentItem;
  onClose: () => void;
}

export function CommentComposer({ robotId, comment, onClose }: CommentComposerProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(comment?.text ?? "");
  const [rating, setRating] = useState<number | null>(comment?.rating ?? null);
  const [error, setError] = useState<string | undefined>(undefined);

  const create = useCreateComment(robotId);
  const edit = useEditComment(robotId, comment?.id ?? -1);
  const mutation = comment ? edit : create;

  const submit = () => {
    setError(undefined);
    mutation.mutate(
      { text, rating },
      {
        onSuccess: onClose,
        onError: (cause) => {
          setError(messageFor(cause, t));
        },
      },
    );
  };

  return (
    <Sheet
      title={comment ? t("robots.comments.editTitle") : t("robots.comments.write")}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          size="cta"
          block
          data-haptic="commit"
          loading={mutation.isPending}
          disabled={text.trim().length < 2}
          onClick={submit}
        >
          {comment ? t("robots.comments.save") : t("robots.comments.send")}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <span className="flex flex-col">
            <span className="text-label font-semibold text-text-primary">
              {t("robots.comments.ratingLabel")}
            </span>
            <span className="text-micro font-normal text-text-tertiary">
              {t("robots.comments.ratingOptional")}
            </span>
          </span>
          <StarRating value={rating} onChange={setRating} size={22} />
        </div>

        <TextArea
          value={text}
          onChange={setText}
          label={t("robots.comments.write")}
          placeholder={t("robots.comments.placeholder")}
          maxLength={COMMENT_MAX_LENGTH}
          error={error}
          hint={t("robots.comments.identityNotice")}
        />
      </div>
    </Sheet>
  );
}

/**
 * Текст ошибки под полем.
 *
 * Приоритет у `details.reason`: он машиночитаем и переводится, тогда как
 * `message` приходит с сервера уже готовой строкой и не переживёт появление
 * второго языка.
 */
function messageFor(cause: unknown, t: TFunction): string {
  if (!(cause instanceof ApiError)) return t("states.error.body");

  const reason = detailString(cause.details, "reason");
  if (reason) {
    const key = `robots.comments.error.${reason}`;
    const translated = t(key);
    if (translated !== key) return translated;
    const blocked = t(`robots.comments.cannot.${reason}`);
    if (blocked !== `robots.comments.cannot.${reason}`) return blocked;
  }
  return cause.message;
}
